/**
 * SideSheet — Tablet-first replacement for modals (UX Invariant §9, rule 3).
 *
 * Blueprint rule: New appointment, appointment detail, payment flow, customer detail
 * etc. open in a right-aligned side sheet — NEVER a centered modal. Side sheets
 * preserve context (the calendar remains visible behind), support multi-step flows
 * without losing state, and keep the primary CTA within thumb reach.
 *
 * Only exception: irreversible confirmations (destructive delete, payment confirm).
 * Those use <ConfirmDialog> — a small centered modal.
 *
 * Built on Radix Dialog for a11y (focus trap, ESC close, aria-labelledby).
 */
import * as Dialog from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';
import { type ReactNode } from 'react';
import { cn } from '../utils/cn';

export interface SideSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Width variant — default 'md' = 560px (tablet-optimized). */
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  footer?: ReactNode;
  /** Close button label for screen readers (localized). */
  closeLabel: string;
}

const sizeMap: Record<NonNullable<SideSheetProps['size']>, string> = {
  sm: 'w-full sm:max-w-md',
  md: 'w-full sm:max-w-xl',
  lg: 'w-full sm:max-w-2xl',
};

export function SideSheet({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  children,
  footer,
  closeLabel,
}: SideSheetProps): JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-biz-sheet',
            'bg-biz-foreground/40 backdrop-blur-sm',
            'data-[state=open]:animate-biz-fade-in',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed top-0 right-0 bottom-0 z-biz-sheet',
            'flex flex-col',
            'bg-biz-surface-elevated text-biz-foreground',
            'shadow-biz-sheet',
            'border-l border-biz-border',
            'focus:outline-none',
            'data-[state=open]:animate-biz-slide-in-right',
            sizeMap[size],
          )}
          aria-describedby={description ? 'side-sheet-desc' : undefined}
        >
          <header className="flex items-start justify-between gap-biz-content-lg px-biz-card py-biz-content-lg border-b border-biz-border">
            <div className="flex-1 min-w-0">
              <Dialog.Title className="text-biz-xl font-semibold biz-heading">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description
                  id="side-sheet-desc"
                  className="mt-biz-content text-biz-sm text-biz-muted-foreground"
                >
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              aria-label={closeLabel}
              className={cn(
                'inline-flex items-center justify-center',
                'min-h-biz-touch min-w-biz-touch rounded-biz-btn',
                'text-biz-muted-foreground hover:bg-biz-muted hover:text-biz-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
                'transition-colors duration-biz-fast',
              )}
            >
              <X weight="regular" size={24} aria-hidden="true" />
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto px-biz-card py-biz-content-lg">
            {children}
          </div>

          {footer ? (
            <footer className="flex items-center justify-end gap-biz-content-lg px-biz-card py-biz-content-lg border-t border-biz-border">
              {footer}
            </footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
